// ===========================================
// CARD WIPE TRANSITION DEMO PAGE
// ===========================================
// Prototype page for experimenting with card back → card face wipe transitions

import { useEffect, useRef, useState, useCallback } from 'react';
import { Application, Assets, Container, Sprite, Graphics, Texture, Text, TextStyle } from 'pixi.js';

// Asset imports - same as PixiGameTableV1
import cardBackUrl from '../assets/cards/card_back.svg';
import aceSpadesUrl from '../assets/cards/AS_face.svg';
import aceHeartsUrl from '../assets/cards/AH_face.svg';
import aceDiamondsUrl from '../assets/cards/AD_face.svg';
import aceClubsUrl from '../assets/cards/AC_face.svg';

// Card dimensions (larger for demo visibility)
const CARD_WIDTH = 150;
const CARD_HEIGHT = 210;

// Suit symbols and colors (same as game)
const SUIT_SYMBOLS: Record<string, string> = {
  hearts: '\u2665',
  diamonds: '\u2666',
  clubs: '\u2663',
  spades: '\u2660',
};

const SUIT_COLORS: Record<string, number> = {
  hearts: 0xef4444,
  diamonds: 0xef4444,
  clubs: 0x111827,
  spades: 0x111827,
};

// Face card URLs
const ACE_URLS: Record<string, string> = {
  spades: aceSpadesUrl,
  hearts: aceHeartsUrl,
  diamonds: aceDiamondsUrl,
  clubs: aceClubsUrl,
};

// Available ranks and suits
const RANKS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const SUITS = ['spades', 'hearts', 'diamonds', 'clubs'];

// Easing functions
const EASINGS = {
  linear: (t: number) => t,
  easeInQuad: (t: number) => t * t,
  easeOutQuad: (t: number) => t * (2 - t),
  easeInOutQuad: (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
  easeInCubic: (t: number) => t * t * t,
  easeOutCubic: (t: number) => 1 - Math.pow(1 - t, 3),
  easeInOutCubic: (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
  easeInExpo: (t: number) => t === 0 ? 0 : Math.pow(2, 10 * t - 10),
  easeOutExpo: (t: number) => t === 1 ? 1 : 1 - Math.pow(2, -10 * t),
  easeInOutExpo: (t: number) => t === 0 ? 0 : t === 1 ? 1 : t < 0.5 ? Math.pow(2, 20 * t - 10) / 2 : (2 - Math.pow(2, -20 * t + 10)) / 2,
};

type EasingName = keyof typeof EASINGS;

interface WipeConfig {
  duration: number;
  angle: number;
  edgeBlur: number;
  easing: EasingName;
  overshoot: number;
}

const DEFAULT_CONFIG: WipeConfig = {
  duration: 500,
  angle: 0,
  edgeBlur: 0,
  easing: 'easeOutCubic',
  overshoot: 0,
};

export default function CardWipeDemo() {
  const canvasRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const cardBackRef = useRef<Sprite | null>(null);
  const cardFaceRef = useRef<Container | null>(null);
  const texturesRef = useRef<Map<string, Texture>>(new Map());
  const animationRef = useRef<number | null>(null);

  const [selectedRank, setSelectedRank] = useState('A');
  const [selectedSuit, setSelectedSuit] = useState('spades');
  const [isShowingFace, setIsShowingFace] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [config, setConfig] = useState<WipeConfig>(DEFAULT_CONFIG);

  // Initialize PixiJS
  useEffect(() => {
    if (!canvasRef.current) return;

    let isActive = true;

    const initPixi = async () => {
      console.log('[CardWipeDemo] Initializing PixiJS...');

      const app = new Application();
      await app.init({
        width: 500,
        height: 400,
        backgroundColor: 0x1a4731,
        antialias: true,
      });

      if (!isActive) return;

      canvasRef.current?.appendChild(app.canvas);
      appRef.current = app;

      const textures = new Map<string, Texture>();

      // Load card back
      try {
        console.log('[CardWipeDemo] Loading card back from:', cardBackUrl);
        const cardBackTexture = await Assets.load(cardBackUrl);
        textures.set('cardBack', cardBackTexture);
        console.log('[CardWipeDemo] Card back loaded successfully');
      } catch (e) {
        console.error('[CardWipeDemo] Failed to load card back:', e);
        return;
      }

      // Load ace textures
      for (const [suit, url] of Object.entries(ACE_URLS)) {
        try {
          const texture = await Assets.load(url);
          textures.set(`A-${suit}`, texture);
          console.log(`[CardWipeDemo] Loaded A-${suit}`);
        } catch (e) {
          console.warn(`[CardWipeDemo] Failed to load A-${suit}:`, e);
        }
      }

      if (!isActive) return;

      texturesRef.current = textures;

      // Create card back sprite (centered in canvas)
      const cardBackTexture = textures.get('cardBack');
      if (cardBackTexture) {
        const cardBack = new Sprite(cardBackTexture);
        cardBack.width = CARD_WIDTH;
        cardBack.height = CARD_HEIGHT;
        cardBack.anchor.set(0.5);
        cardBack.x = 250;
        cardBack.y = 200;
        app.stage.addChild(cardBack);
        cardBackRef.current = cardBack;
        console.log('[CardWipeDemo] Card back sprite created at', cardBack.x, cardBack.y);
      }

      // Create card face container
      const cardFace = createCardFaceContainer('A', 'spades', textures);
      cardFace.x = 250;
      cardFace.y = 200;
      cardFace.visible = false;
      app.stage.addChild(cardFace);
      cardFaceRef.current = cardFace;
      console.log('[CardWipeDemo] Card face container created, children:', cardFace.children.length);

      setIsReady(true);
      console.log('[CardWipeDemo] Initialization complete');
    };

    initPixi();

    return () => {
      isActive = false;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      appRef.current?.destroy(true);
      appRef.current = null;
    };
  }, []);

  // Create a card face container (matching PixiGameTableV1 approach)
  const createCardFaceContainer = useCallback((rank: string, suit: string, textures: Map<string, Texture>): Container => {
    const container = new Container();
    const color = SUIT_COLORS[suit];

    // Card shadow
    const shadow = new Graphics();
    shadow.roundRect(-CARD_WIDTH / 2 + 2, -CARD_HEIGHT / 2 + 2, CARD_WIDTH, CARD_HEIGHT, 6);
    shadow.fill({ color: 0x000000, alpha: 0.3 });
    container.addChild(shadow);

    // Card background
    const bg = new Graphics();
    bg.roundRect(-CARD_WIDTH / 2, -CARD_HEIGHT / 2, CARD_WIDTH, CARD_HEIGHT, 6);
    bg.fill({ color: 0xfefefe });
    bg.stroke({ color: 0x374151, width: 1.5 });
    container.addChild(bg);

    // For Aces, use the face texture
    if (rank === 'A') {
      const textureKey = `A-${suit}`;
      const faceTexture = textures.get(textureKey);
      if (faceTexture) {
        const faceSprite = new Sprite(faceTexture);
        faceSprite.width = 132;  // Scaled up 2x
        faceSprite.height = 188;
        faceSprite.anchor.set(0.5);
        container.addChild(faceSprite);
      }
    } else {
      // Number cards - center suit symbol
      const centerStyle = new TextStyle({
        fontFamily: 'Arial',
        fontSize: 96,  // Scaled up 2x
        fill: color,
      });
      const centerText = new Text({ text: SUIT_SYMBOLS[suit], style: centerStyle });
      centerText.anchor.set(0.5);
      centerText.y = -8;
      container.addChild(centerText);
    }

    // Corner rank style
    const cornerRankStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 28,  // Scaled up 2x
      fontWeight: 'bold',
      fill: color,
    });

    // Corner suit style
    const cornerSuitStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 24,  // Scaled up 2x
      fontWeight: 'bold',
      fill: color,
    });

    // Top-left corner
    const rankText = new Text({ text: rank, style: cornerRankStyle });
    rankText.x = -CARD_WIDTH / 2 + 8;
    rankText.y = -CARD_HEIGHT / 2 + 4;
    container.addChild(rankText);

    const suitText = new Text({ text: SUIT_SYMBOLS[suit], style: cornerSuitStyle });
    suitText.x = -CARD_WIDTH / 2 + 8;
    suitText.y = -CARD_HEIGHT / 2 + 36;
    container.addChild(suitText);

    // Bottom-right corner (rotated)
    const rankText2 = new Text({ text: rank, style: cornerRankStyle });
    rankText2.x = CARD_WIDTH / 2 - 8;
    rankText2.y = CARD_HEIGHT / 2 - 4;
    rankText2.anchor.set(0, 0);
    rankText2.rotation = Math.PI;
    container.addChild(rankText2);

    const suitText2 = new Text({ text: SUIT_SYMBOLS[suit], style: cornerSuitStyle });
    suitText2.x = CARD_WIDTH / 2 - 8;
    suitText2.y = CARD_HEIGHT / 2 - 36;
    suitText2.anchor.set(0, 0);
    suitText2.rotation = Math.PI;
    container.addChild(suitText2);

    return container;
  }, []);

  // Update card face when selection changes
  useEffect(() => {
    if (!appRef.current || !cardFaceRef.current || !isReady) return;

    const app = appRef.current;
    const oldFace = cardFaceRef.current;

    // Remove and destroy old face
    app.stage.removeChild(oldFace);
    oldFace.destroy({ children: true });

    // Create new face
    const newFace = createCardFaceContainer(selectedRank, selectedSuit, texturesRef.current);
    newFace.x = 250;
    newFace.y = 200;
    newFace.visible = isShowingFace;
    app.stage.addChild(newFace);
    cardFaceRef.current = newFace;

    console.log('[CardWipeDemo] Card face updated to', selectedRank, selectedSuit);
  }, [selectedRank, selectedSuit, createCardFaceContainer, isReady, isShowingFace]);

  // Debug: Show face instantly
  const showFaceDebug = useCallback(() => {
    if (!cardBackRef.current || !cardFaceRef.current) {
      console.log('[Debug] Refs not ready:', !!cardBackRef.current, !!cardFaceRef.current);
      return;
    }
    console.log('[Debug] Showing card face directly');
    cardBackRef.current.visible = false;
    cardFaceRef.current.visible = true;
    setIsShowingFace(true);
  }, []);

  // Reset to card back
  const resetCard = useCallback(() => {
    if (isAnimating) return;
    if (cardBackRef.current) {
      cardBackRef.current.visible = true;
    }
    if (cardFaceRef.current) {
      cardFaceRef.current.visible = false;
    }
    setIsShowingFace(false);
    console.log('[CardWipeDemo] Reset to card back');
  }, [isAnimating]);

  // Perform wipe animation
  const performWipe = useCallback(() => {
    if (isAnimating || !cardBackRef.current || !cardFaceRef.current || !appRef.current) return;

    console.log('[CardWipeDemo] Starting wipe animation');
    setIsAnimating(true);
    const startTime = performance.now();
    const { duration, angle, edgeBlur, easing, overshoot } = config;
    const easingFn = EASINGS[easing];

    const cardBack = cardBackRef.current;
    const cardFace = cardFaceRef.current;
    const app = appRef.current;

    // Create mask for the wipe effect
    const mask = new Graphics();

    // Draw initial empty state (nothing revealed yet)
    mask.rect(-CARD_WIDTH, -CARD_HEIGHT, 1, 1); // Tiny rect
    mask.fill({ color: 0xffffff });

    cardFace.addChild(mask);
    cardFace.mask = mask;

    // Note: Edge blur on masks doesn't work well in PixiJS
    void edgeBlur;

    // Show card face on top
    cardFace.visible = true;
    app.stage.removeChild(cardFace);
    app.stage.addChild(cardFace);

    console.log('[CardWipeDemo] Config:', { duration, angle, easing, overshoot });

    // Wipe parameters (card-relative local coordinates)
    const angleRad = (angle * Math.PI) / 180;
    const dx = Math.cos(angleRad);
    const dy = Math.sin(angleRad);
    const diagonal = Math.sqrt(CARD_WIDTH * CARD_WIDTH + CARD_HEIGHT * CARD_HEIGHT);
    const totalDistance = diagonal + 40;

    // Overshoot: extend the animation slightly beyond 100% then snap back
    const overshootFactor = 1 + (overshoot / 100);

    let frameCount = 0;
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const rawProgress = Math.min(elapsed / duration, 1);

      // Apply easing
      let progress = easingFn(rawProgress);

      // Apply overshoot - progress can go beyond 1.0, then clamp for final frame
      if (overshoot > 0 && rawProgress < 1) {
        progress = progress * overshootFactor;
      }
      // Clamp to 1.0 max for the mask drawing
      const clampedProgress = Math.min(progress, 1);

      frameCount++;
      if (frameCount <= 3 || rawProgress >= 0.99) {
        console.log(`[Animate] frame=${frameCount} raw=${rawProgress.toFixed(3)} eased=${progress.toFixed(3)} clamped=${clampedProgress.toFixed(3)}`);
      }

      // Draw mask in local coordinates
      mask.clear();

      // Wipe line moves from -totalDistance/2 to +totalDistance/2
      const startOffset = -totalDistance / 2;
      const currentOffset = startOffset + clampedProgress * totalDistance;

      // Perpendicular direction for mask width
      const perpX = -dy;
      const perpY = dx;
      const extent = diagonal;

      // Revealed region: from far behind to current wipe line
      const behindOffset = -totalDistance;
      mask.moveTo(dx * behindOffset + perpX * extent, dy * behindOffset + perpY * extent);
      mask.lineTo(dx * behindOffset - perpX * extent, dy * behindOffset - perpY * extent);
      mask.lineTo(dx * currentOffset - perpX * extent, dy * currentOffset - perpY * extent);
      mask.lineTo(dx * currentOffset + perpX * extent, dy * currentOffset + perpY * extent);
      mask.closePath();
      mask.fill({ color: 0xffffff });

      if (rawProgress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Complete
        cardBack.visible = false;
        cardFace.mask = null;
        cardFace.removeChild(mask);
        mask.destroy();
        setIsAnimating(false);
        setIsShowingFace(true);
        console.log('[CardWipeDemo] Wipe complete');
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  }, [config, isAnimating]);

  const updateConfig = <K extends keyof WipeConfig>(key: K, value: WipeConfig[K]) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <h1 className="text-2xl font-bold mb-6">Card Wipe Transition Demo</h1>

      <div className="flex gap-8">
        {/* Canvas */}
        <div className="flex-shrink-0">
          <div
            ref={canvasRef}
            className="border-2 border-gray-700 rounded-lg overflow-hidden"
            style={{ width: 500, height: 400 }}
          />

          {/* Status */}
          <div className="mt-2 text-sm text-gray-400">
            Status: {isReady ? '✅ Ready' : '⏳ Loading...'}
          </div>

          {/* Action buttons */}
          <div className="flex gap-4 mt-4">
            <button
              onClick={performWipe}
              disabled={isAnimating || isShowingFace || !isReady}
              className="px-6 py-2 bg-green-600 hover:bg-green-500 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-semibold transition-colors"
            >
              Wipe to Face
            </button>
            <button
              onClick={resetCard}
              disabled={isAnimating || !isShowingFace}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-semibold transition-colors"
            >
              Reset
            </button>
            <button
              onClick={showFaceDebug}
              disabled={isShowingFace || !isReady}
              className="px-6 py-2 bg-yellow-600 hover:bg-yellow-500 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-semibold transition-colors"
            >
              Debug: Show
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="flex-1 space-y-6">
          {/* Card Selection */}
          <div className="bg-gray-800 p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-3">Card Value</h2>
            <div className="flex gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Rank</label>
                <select
                  value={selectedRank}
                  onChange={(e) => setSelectedRank(e.target.value)}
                  className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  {RANKS.map(rank => (
                    <option key={rank} value={rank}>{rank}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Suit</label>
                <select
                  value={selectedSuit}
                  onChange={(e) => setSelectedSuit(e.target.value)}
                  className="bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                >
                  {SUITS.map(suit => (
                    <option key={suit} value={suit}>{suit} {SUIT_SYMBOLS[suit]}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Note: Only Aces have custom face graphics loaded</p>
          </div>

          {/* Wipe Configuration */}
          <div className="bg-gray-800 p-4 rounded-lg">
            <h2 className="text-lg font-semibold mb-3">Wipe Configuration</h2>

            {/* Duration */}
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-1">
                Duration: {config.duration}ms
              </label>
              <input
                type="range"
                min="100"
                max="2000"
                step="50"
                value={config.duration}
                onChange={(e) => updateConfig('duration', parseInt(e.target.value))}
                className="w-full"
              />
            </div>

            {/* Angle */}
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-1">
                Angle: {config.angle}°
              </label>
              <input
                type="range"
                min="0"
                max="359"
                step="1"
                value={config.angle}
                onChange={(e) => updateConfig('angle', parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex gap-2 mt-2">
                {[0, 90, 180, 270].map(a => (
                  <button
                    key={a}
                    onClick={() => updateConfig('angle', a)}
                    className={`px-3 py-1 text-sm rounded ${config.angle === a ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                  >
                    {a}°
                  </button>
                ))}
              </div>
            </div>

            {/* Edge Blur */}
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-1">
                Edge Blur: {config.edgeBlur}px
              </label>
              <input
                type="range"
                min="0"
                max="50"
                step="1"
                value={config.edgeBlur}
                onChange={(e) => updateConfig('edgeBlur', parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>0 (sharp)</span>
                <span>50 (soft)</span>
              </div>
            </div>

            {/* Easing */}
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-1">Easing</label>
              <select
                value={config.easing}
                onChange={(e) => updateConfig('easing', e.target.value as EasingName)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
              >
                {Object.keys(EASINGS).map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>

            {/* Overshoot */}
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-1">
                Overshoot: {config.overshoot}%
              </label>
              <input
                type="range"
                min="0"
                max="50"
                step="5"
                value={config.overshoot}
                onChange={(e) => updateConfig('overshoot', parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>0%</span>
                <span>50%</span>
              </div>
            </div>
          </div>

          {/* Current Config Display */}
          <div className="bg-gray-800 p-4 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-semibold">Current Config</h2>
              <button
                onClick={() => navigator.clipboard.writeText(JSON.stringify(config, null, 2))}
                className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded transition-colors"
              >
                Copy
              </button>
            </div>
            <pre className="text-xs text-gray-400 bg-gray-900 p-3 rounded overflow-x-auto font-mono">
              {JSON.stringify(config, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
